import { MdAccessTime, MdLocationOn, MdBookmark, MdBookmarkBorder } from "react-icons/md";
import { Offer } from "../../../models/offers";

type OfferCardProps = {
    offer: Offer;
    onView: (offer: Offer) => void;
    isAdmin?: boolean;
    isSaved?: boolean;
    onToggleSave?: (offerId: any) => Promise<void>;
};

export function OfferCardAll({ offer, onView, isAdmin = false, isSaved = false, onToggleSave }: OfferCardProps) {

    const scheduleDays = offer.schedule?.days ?? [];
    const isPayoutFixed = (offer.payout_type || "").toLowerCase() === "fixed";
    const payoutDisplay = isPayoutFixed ? `$${offer.payout}` : "Variable";

    const hasDuration = (offer.connected_duration !== null && offer.connected_duration !== undefined) || (offer.call_type?.flex || "").toLowerCase() === "variable";
    const isDurationFixed = (offer.call_type?.flex || "").toLowerCase() === "fixed";
    const durationDisplay = isDurationFixed ? `/${offer.connected_duration}s` : "";

    return (
        <div
            className={`rounded-2xl shadow hover:shadow-lg transition p-4 flex flex-col ${offer.live ? 'bg-white' : 'bg-gray-200 opacity-80'
                }`}
            style={{
                fontFamily:
                    'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial',
            }}
        >
            {/* Category and Price */}
            <div className="flex items-center justify-between mb-2">
                <div className="text-[#F97316] uppercase text-[12px] tracking-[0.14em] font-semibold" style={{ fontFamily: 'Inter, ui-sans-serif' }}>
                    {offer.vertical}
                </div>
                <div>
                    <span className="shrink-0 inline-flex items-center gap-1 px-4 py1 rounded-lg bg-orange-50 text-[#F97316] text-[15px] font-semibold mr-1">
                        {offer.campaign_type}
                    </span>
                    <span className="shrink-0 inline-flex items-center gap-1 px-4 py1 rounded-lg bg-orange-50 text-[#F97316] text-[15px] font-semibold">
                        {payoutDisplay}{hasDuration && <span className="font-medium text-[13px] opacity-80">{durationDisplay}</span>}
                    </span>
                </div>
            </div>

            {/* Title */}
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-[18px] leading-6 font-semibold text-[#3f3f3f] break-words">
                    {offer.category}
                </h2>
            </div>

            {/* Details */}
            <div className="space-y-3 mb-4">
                {/* Schedule */}
                <div className="flex items-start gap-3">
                    <MdAccessTime className="text-[#F97316] mt-1" size={20} />
                    <div className="flex-1">
                        <div className="text-[12px] font-semibold text-[#F97316] uppercase mb-1">Schedule</div>
                        <div className="text-[13px] text-[#3f3f3f]">
                            {scheduleDays.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                    {scheduleDays.map((day, i) => (
                                        <span key={i} className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] text-[#3f3f3f]">{day.slice(0, 3)}</span>
                                    ))}
                                </div>
                            ) : (
                                <span className="text-gray-500 text-[12px]">No schedule listed</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Coverage */}
                <div className="flex items-start gap-3">
                    <MdLocationOn className="text-[#F97316] mt-1" size={20} />
                    <div className="flex-1">
                        <div className="text-[12px] font-semibold text-[#F97316] uppercase mb-1">Coverage</div>
                        <div className="text-[13px] text-[#3f3f3f]">
                            {offer.coverage.state_list && offer.coverage.state_list.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                    {offer.coverage.state_list.slice(0, 5).map((region, i) => (
                                        <span key={i} className="px-2 py-1 bg-gray-100 rounded text-[11px] text-[#3f3f3f]">
                                            {region === "Nationwide"
                                                ? "🌍 Nationwide"
                                                : /^[A-Z]{2}$/.test(region)
                                                    ? `🏛️ ${region}`
                                                    : /^[A-Z]{2,3}$/.test(region)
                                                        ? `🌐 ${region}`
                                                        : region}
                                        </span>
                                    ))}
                                    {offer.coverage.state_list.length > 5 && (
                                        <span className="px-2 py-1 bg-gray-200 rounded text-[11px] text-[#3f3f3f]">
                                            +{offer.coverage.state_list.length - 5} more
                                        </span>
                                    )}
                                </div>
                            ) : offer.coverage.number_of_zip_codes ? (
                                <span className="text-gray-500 text-[12px]">📍{offer.coverage.number_of_zip_codes} Zip Codes</span>
                            ) : (
                                <span className="text-gray-500 text-[12px]">No coverage listed</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Buttons */}
            <div className={`flex gap-2 mt-auto ${isAdmin ? "" : "justify-between"}`}>
                <button
                    onClick={() => onView(offer)}
                    className="flex-1 bg-[#F97316] hover:bg-orange-600 text-white rounded-xl px-7 py-2 text-[16px] font-semibold shadow transition"
                >
                    View Details
                </button>
                {!isAdmin && onToggleSave && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onToggleSave(offer.id); }}
                        className="p-3.5 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                        {isSaved ? <MdBookmark className="text-[#F97316] text-xl" /> : <MdBookmarkBorder className="text-gray-400 hover:text-[#F97316] text-xl" />}
                    </button>
                )}
            </div>
        </div>
    );
}
